<template>
  <div class="app-wrapper" :class="pageClass">
    <div ref="alertStackEl" class="app-alerts">
      <ImpersonationBanner />
      <OrganizationSuspendedBanner />
    </div>
    <router-view />
    <ModalHandler />
    <DrawerHandler />
    <ToastHandler />
  </div>
</template>

<script>
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import ModalHandler from '@/components/modal/ModalHandler.vue';
  import DrawerHandler from '@/components/drawer/DrawerHandler.vue';
  import ToastHandler from '@/components/toast/ToastHandler.vue';
  import ImpersonationBanner from '@/components/platform/ImpersonationBanner.vue';
  import OrganizationSuspendedBanner from '@/components/layout/OrganizationSuspendedBanner.vue';
  import { useDynamicFavicon } from '@/composables/useDynamicFavicon';
  import { useUserStore } from '@/store/userStore';
  import { identifyChatwootUser, resetChatwootSession } from '@/plugins/chatwoot';

  export default {
    name: 'App',
    components: {
      ModalHandler,
      DrawerHandler,
      ToastHandler,
      ImpersonationBanner,
      OrganizationSuspendedBanner,
    },
    setup() {
      const route = useRoute();
      const userStore = useUserStore();
      const alertStackEl = ref(null);
      const pageClass = computed(() => {
        return `page-${route.name?.toLowerCase()}`;
      });

      const syncAlertOffset = () => {
        const height = alertStackEl.value?.offsetHeight || 0;
        document.documentElement.style.setProperty('--app-alert-offset', `${height}px`);
      };

      let alertObserver = null;
      onMounted(() => {
        const el = alertStackEl.value;
        if (el && typeof ResizeObserver !== 'undefined') {
          alertObserver = new ResizeObserver(syncAlertOffset);
          alertObserver.observe(el);
        }
        syncAlertOffset();
      });

      onUnmounted(() => {
        alertObserver?.disconnect();
        document.documentElement.style.removeProperty('--app-alert-offset');
      });

      // Initialize dynamic favicon based on organization branding
      useDynamicFavicon();

      // Keep Chatwoot in sync with the current user / organization
      watch(
        () => [userStore.user?.id, userStore.currentOrganization?.id],
        () => {
          const user = userStore.user;
          if (!user) {
            resetChatwootSession();
            return;
          }
          if (document.documentElement.dataset.anonymous === 'true') {
            resetChatwootSession();
            return;
          }
          const meta = user.user_metadata || {};
          const fullName = [meta.first_name, meta.last_name].filter(Boolean).join(' ').trim();
          identifyChatwootUser({
            email: user.email,
            nickname: fullName || user.email,
            userId: user.id,
            avatarUrl: meta.avatar_url,
            organizationName: userStore.currentOrganization?.name,
            organizationId: userStore.currentOrganization?.id,
          });
        },
        { immediate: true }
      );

      return {
        pageClass,
        alertStackEl,
      };
    },
  };
</script>

<style>
  .app-wrapper {
    padding-top: var(--app-alert-offset, 0px);
  }

  .app-wrapper.page-automationeditor {
    .app {
      height: calc(100vh - var(--app-alert-offset, 0px));
      min-height: 0;
    }

    .app__main {
      padding: 0;
      height: calc(100% - 1rem);
      min-height: 0;
      overflow: hidden;
    }

    .app__main-content {
      padding: 0;
      height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }

  .app-alerts {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1500;
  }

  .app-alerts:has(.org-suspended, .impersonation-banner) {
    padding-top: env(safe-area-inset-top, 0);
  }

  .modal-backdrop-enter-active,
  .modal-backdrop-leave-active {
    transition: opacity 0.3s ease;
  }

  .modal-backdrop-enter,
  .modal-backdrop-leave-to {
    opacity: 0;
  }

  .modal-inner-enter-active,
  .modal-inner-leave-active {
    transition: transform 0.3s ease;
  }

  .modal-inner-enter,
  .modal-inner-leave-to {
    transform: translateY(100%);
  }
</style>
